import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { BoardCanvas, CanvasMode, convertVisualizationToElements } from '@ideoz/canvas';
import type { CanvasApi } from '@ideoz/canvas';
import type {
  HowMightWeData,
  ProblemStatementsData,
  InterviewQuestionsData,
  UserStoriesData,
  SurveyQuestionsData,
  FiveWhysData,
  PersonaData,
  JourneyMapData,
  MindMapData,
  UserFlowChartData,
  BenchmarkReportData,
} from '@ideoz/canvas/types/visualizations';

type VisualizationType = 
  | 'howMightWe' 
  | 'problemStatements' 
  | 'interviewQuestions' 
  | 'userStories'
  | 'surveyQuestions'
  | 'fiveWhys'
  | 'persona'
  | 'journeyMap'
  | 'mindMap'
  // | 'userFlowChart'

interface CanvasExportViewProps {
  type: VisualizationType;
  data: HowMightWeData | ProblemStatementsData | InterviewQuestionsData | UserStoriesData | SurveyQuestionsData | FiveWhysData | PersonaData | JourneyMapData | MindMapData | UserFlowChartData | BenchmarkReportData;
}

/**
 * Canvas Export View - Renders FigJam visualizations on web canvas
 * Provides PNG export functionality without Figma/FigJam dependency
 */
export const CanvasExportView: React.FC<CanvasExportViewProps> = ({ type, data }) => {
  const canvasApiRef = useRef<CanvasApi | null>(null);
  const [exporting, setExporting] = useState(false);
  const [mode, setMode] = useState<CanvasMode>('edit');
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 1600, height: 900 });

  // Handle canvas ready
  const handleCanvasReady = useCallback((api: CanvasApi) => {
    canvasApiRef.current = api;
  }, []);

  // Update canvas size based on container dimensions
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setCanvasSize({ width: clientWidth, height: clientHeight });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  /**
   * Export canvas as PNG
   */
  const handleExport = useCallback(() => {
    if (!canvasApiRef.current) return;

    setExporting(true);

    try {
      const dataUrl = canvasApiRef.current.toDataURL({
        pixelRatio: 2, // High quality export
      });

      // Trigger download
      const link = document.createElement('a');
      link.download = `${type}-${Date.now()}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export image. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [type]);

  /**
   * Convert visualization data to editable elements
   */
  const elements = useMemo(() => {
    return convertVisualizationToElements(type, data);
  }, [type, data]);

  return (
    <div style={{ padding: '20px', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
            Canvas Visualization
          </h1>
      
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          style={{
            padding: '10px 20px',
            backgroundColor: '#0066FF',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: exporting ? 'not-allowed' : 'pointer',
            opacity: exporting ? 0.6 : 1,
          }}
        >
          {exporting ? 'Exporting...' : 'Export as PNG'}
        </button>
      </div>

      <div 
        ref={containerRef}
        style={{ 
          border: '1px solid #E5E5E5', 
          borderRadius: '8px', 
          overflow: 'hidden',
          backgroundColor: '#F5F5F5',
          height: 'calc(100vh - 250px)',
        }}>
        <BoardCanvas 
          key={type} // Force remount when visualization type changes
          mode={mode}
          onModeChange={setMode}
          width={canvasSize.width} 
          height={canvasSize.height}
          initialElements={elements}
          onCanvasReady={handleCanvasReady}
        />
      </div>
    </div>
  );
};

/**
 * Demo page with sample data for testing
 */
export const CanvasExportDemo: React.FC = () => {
  const [selectedType, setSelectedType] = useState<VisualizationType>('howMightWe');

  // Sample data
  const sampleData: Record<VisualizationType, any> = {
    howMightWe: {
      questions: [
        'How might we improve user onboarding experience?',
        'How might we reduce customer support tickets?',
        'How might we increase user engagement?',
        'How might we make the interface more intuitive?',
        'How might we help users achieve their goals faster?',
        'How might we improve user onboarding experience?',
        'How might we reduce customer support tickets?',
        'How might we increase user engagement?',
        'How might we make the interface more intuitive?',
        'How might we help users achieve their goals faster?',
      ],
    },
    problemStatements: {
      statements: [
        {
          title: 'Navigation Issues',
          subtitle: 'Users struggle to find key features',
          description: 'Current menu structure is confusing and important actions are buried',
        },
        {
          title: 'Slow Performance',
          subtitle: 'Page load times exceed 3 seconds',
          description: 'Users report frustration with slow response times',
        },
        {
          title: 'Mobile Responsiveness',
          subtitle: 'App doesn\'t work well on smaller screens',
          description: 'Layout breaks on mobile devices and buttons are too small',
        },
      ],
    },
    interviewQuestions: {
      title: 'User Interview Questions',
      description: 'Questions to understand user needs, behaviors, and pain points during the discovery phase',
      questions: [
        {
          question: 'Can you walk me through your typical workflow when starting a new project?',
          follow_up: [
            'What tools do you currently use in this process?',
            'What are the biggest challenges you face during project initiation?',
            'How much time does this typically take?',
          ],
        },
        {
          question: 'How do you currently track and manage project requirements?',
          follow_up: [
            'What pain points do you experience with your current system?',
            'How do you ensure nothing gets missed?',
            'How do you communicate changes to your team?',
          ],
        },
        {
          question: 'Describe a time when miscommunication caused issues in a project.',
          follow_up: [
            'What could have prevented this situation?',
            'How did you resolve it?',
            'What lessons did you learn?',
          ],
        },
        {
          question: 'What features would make your work significantly easier?',
          follow_up: [
            'Have you tried any solutions for this?',
            'What would be the ideal workflow for you?',
            'How would this impact your daily productivity?',
          ],
        },
        {
          question: 'How do you collaborate with team members across different time zones or departments?',
          follow_up: [
            'What collaboration tools are working well for you?',
            'What gaps exist in your current collaboration setup?',
            'How do you handle urgent requests outside business hours?',
          ],
        },
      ],
      focus_areas: [
        'Workflow efficiency and time-saving opportunities',
        'Pain points in current tools and processes',
        'Communication and collaboration challenges',
        'Feature prioritization based on user needs',
        'Integration requirements with existing systems',
        'User expectations and success metrics',
      ],
    },
    userStories: {
      title: 'User Stories',
      stories: [
        {
          story: 'As a user, I want to easily navigate the application so that I can find features quickly',
          acceptance_criteria: [
            'Main menu is visible on all pages',
            'Search functionality works across all sections',
            'Navigation responds within 1 second',
          ],
        },
        {
          story: 'As a designer, I want to export my work in multiple formats so that I can share with my team',
          acceptance_criteria: [
            'Support PNG, SVG, and PDF exports',
            'High-quality output at 2x resolution',
            'Export completes in under 5 seconds',
          ],
        },
        {
          story: 'As an admin, I want to manage user permissions so that I can control access to features',
          acceptance_criteria: [
            'Role-based access control',
            'Audit log of permission changes',
          ],
        },
      ],
    },
    surveyQuestions: {
      title: 'Survey questions',
      description: 'Here are 10 survey questions to collect quantitative information from accountants, focusing on their experience with ordering healthy food via a mobile app, their preferences, and their challenges:',
      questions: [
        {
          question: 'How often do you use a mobile app to order food during your workday?',
          answers: [
            { letter: 'a', text: 'Daily' },
            { letter: 'b', text: '2-3 times a week' },
            { letter: 'c', text: 'Once a week' },
            { letter: 'd', text: 'Rarely or never' },
            { letter: 'e', text: 'It Naver' },
          ],
        },
        {
          question: 'How important is it for you to order healthy food during work hours?',
          answers: [
            { letter: 'a', text: 'A) Very important' },
            { letter: 'b', text: 'B) Somewhat important' },
            { letter: 'c', text: 'C) Neutral' },
            { letter: 'd', text: 'D) Not very important' },
            { letter: 'e', text: 'E) Not important at all' },
          ],
        },
        {
          question: 'On a scale of 1-5, how satisfied are you with the current meal options available through your food delivery app?',
          answers: [
            { letter: '1', text: 'Very dissatisfied' },
            { letter: '2', text: 'Neutral' },
            { letter: '3', text: 'Neutral' },
            { letter: '4', text: 'Satisfied' },
            { letter: '5', text: 'Very Satisfied' },
          ],
        },
      ],
    },
    fiveWhys: {
      problemCategories: [
        {
          title: 'Users abandon checkout',
          questions: [
            { question: 'Payment process is complicated', answer: 'No clear payment options displayed' },
            { question: 'Too many form fields required', answer: 'Form asks for 15+ fields' },
            { question: 'Users don\'t trust the security', answer: 'No security badges visible' },
            { question: 'No guest checkout option', answer: 'Registration is mandatory' },
            { question: 'Unclear shipping costs', answer: 'Costs only shown at final step' },
          ],
        },
        {
          title: 'High support tickets',
          questions: [
            { question: 'Features are hard to find', answer: 'Menu structure is 4 levels deep' },
            { question: 'Documentation is incomplete', answer: 'Only 30% of features documented' },
            { question: 'UI is not intuitive', answer: 'Users need training to use basic features' },
            { question: 'No onboarding tutorial', answer: 'Users left to explore on their own' },
          ],
        },
      ],
    },
    persona: {
      name: 'Ahmad Tarek',
      age: 33,
      position: 'Senior Accountant',
      location: 'Cairo, Egypt',
      gender: 'male' as const,
      infoText: [
        '• Fast-paced, medium size business',
        '• Motivated and eager learning'
      ],
      bioText: 'Ahmad is a Senior Accountant with over 8 years of experience working in a fast-paced, medium-sized company in Cairo. His role requires him to manage complex financial operations and reports under tight deadlines. Ahmad thrives in this dynamic work environment, balancing multiple tasks while ensuring accuracy and compliance with Egypt regulations. Despite his busy schedule, Ahmad is highly motivated to learn and improve his skills in both accounting and personal wellness.',
      personalityProgress: [
        { text1: 'Introvert', text2: 'Extrovert', score: 35 },
        { text1: 'Analytical', text2: 'Creative', score: 75 },
        { text1: 'Busy', text2: 'Time rich', score: 25 },
        { text1: 'Messy', text2: 'Organized', score: 60 },
        { text1: 'Independent', text2: 'Team-player', score: 80 },
        { text1: 'Passive', text2: 'Active', score: 85 },
        { text1: 'Safe', text2: 'Risky', score: 45 },
      ],
      stickySections: [
        {
          title: 'Goals',
          notes: [
            ['Maintain a healthy lifestyle despite his demanding job'],
            ['Save time on meal planning and preparation'],
            ['Discover new healthy meal options'],
            ['Support local businesses that prioritize quality']
          ]
        },
        {
          title: 'Challenges',
          notes: [
            ['Limited time during work hours for meal breaks'],
            ['Difficulty finding healthy meal options that fit his schedule'],
            ['Balancing work deadlines with personal wellness'],
            ['Skepticism about food delivery apps meeting his health standards']
          ]
        },
        {
          title: 'Ahmad\'s context',
          notes: [
            ['Works 9-5 with occasional overtime during busy periods'],
            ['Often skips breakfast due to early morning commute'],
            ['Prefers quick, nutritious meals during lunch breaks'],
            ['Lives alone and values convenience in meal preparation']
          ]
        },
        {
          title: 'Motivations',
          notes: [
            ['Desire to maintain energy levels throughout the workday'],
            ['Interest in improving overall health and fitness'],
            ['Appreciation for convenient, time-saving solutions']
          ]
        },
        {
          title: 'Needs',
          notes: [
            ['Quick and easy ordering process'],
            ['Clear nutritional information for menu items'],
            ['Reliable delivery within specified time windows']
          ]
        },
        {
          title: 'Jobs to be done',
          notes: [
            ['Order healthy lunch during work hours'],
            ['Track nutritional intake'],
            ['Discover new healthy restaurants'],
            ['Schedule meals in advance']
          ]
        }
      ]
    },
    journeyMap: {
      persona: {
        profile: 'Ahmad Alrajhi: 21 year old engineering student, balancing heavy coursework with part-time tutoring.',
        goal: ''
      },
      phases: [
        {
          phase: 'Awareness',
          actions: ['First seeing a specific item (quality, style) at a location. Friend or someone close likes or "colludes"', 'Need satisfaction or "calibres" (Friend or someone close likes or recommendations). To buy: locate(s) "to buy locate(s)"'],
          thoughts: ['First Impression or sight Memory', 'To use Item or calibration'],
          emotions: ['Having Positive attract', 'Excited'],
          opportunities: ['Contextual prompting in onboarding', 'from bundle-to 3 suggestions', 'Tooltip/badge: "save feeds" in icon bundle or if assistant assists apps'],
        },
        {
          phase: 'Product Discovery',
          actions: ['Locate this right platform or browse platform', 'review style Locate or Item items', 'Comparison platform pricing', 'Product Discovery'],
          thoughts: ['Some items are at a platform how I locate this right platform or browse product let', 'Platform or store reviews – what app?'],
          emotions: ['Keen interest'],
          opportunities: ['At locate platform show bundle search clearly locate clearly', 'By items suggestion relevant suggestion appear relevant suggestion'],
        },
        {
          phase: 'Product Evaluation',
          actions: ['Find positive or specific item (quality, review) at locate icon', 'Feed locate platform or calibrates friend', 'Compare style or locate'],
          thoughts: ['Product at valid my locate or my choose', 'Valid for item am I correct?'],
          emotions: ['Feel correct'],
          opportunities: ['Common prompts show helpful or suggestion', 'List locate icon or store'],
        },
        {
          phase: 'Add to Cart',
          actions: ['Locate this right product feed', 'Size or calibrate select', 'Product Evaluation'],
          thoughts: ['Need valid list or platform locate product'],
          emotions: ['Valid feed or list correct or Item or platform locate show show'],
          opportunities: ['Product Evaluation feed product'],
        },
        {
          phase: 'Checkout Process',
          actions: ['First locate icon platform store', 'Size calibrate platform', 'Review style or list'],
          thoughts: ['Is this correct method or feed?'],
          emotions: ['List correct feed'],
          opportunities: ['Feed locate icon platform store or icon feed platform correct'],
        },
        {
          phase: 'Payment Confirmation',
          actions: ['Feed locate icon platform store feed', 'Text or platform locate', 'Confirmation style review'],
          thoughts: ['Valid feed icon or list?'],
          emotions: ['Review locate feed correct feed or suggestion'],
          opportunities: ['Check feed or locate icon platform store', 'Show locate feed or icon'],
        },
        {
          phase: 'Fulfillment & Delivery',
          actions: ['Feed locate icon platform store', 'Text or show feed', 'Review text or feed locate'],
          thoughts: ['Feed locate icon or platform?'],
          emotions: ['Icon list feed correct or locate'],
          opportunities: ['Review list feed correct'],
        },
        {
          phase: 'Retention & Advocacy',
          actions: ['Feed review locate icon', 'Show text feed or list', 'Feed review icon'],
          thoughts: ['Review feed icon or platform locate?'],
          emotions: ['Review correct feed or platform list'],
          opportunities: ['Icon feed review correct'],
        },
      ],
    },
    mindMap: {
      title: 'Product Launch Strategy',
      nodes: [
        {
          text: 'Marketing',
          children: [
            { text: 'Social Media' },
            { text: 'Email Campaign' },
            { text: 'Press Release' },
          ],
        },
        {
          text: 'Development',
          children: [
            { text: 'Core Features' },
            { text: 'QA Testing' },
            { text: 'Deployment' },
          ],
        },
        {
          text: 'Sales',
          children: [
            { text: 'Pricing Strategy' },
            { text: 'Customer Outreach' },
          ],
        },
      ],
    },
    // userFlowChart: {
    //   title: 'User Flow Chart',
    //   userStory: {
    //     title: 'Create account on the platform',
    //     persona: 'parent',
    //     action: 'create account on the platform',
    //     need: 'follow my kids progress',
    //   },
    //   legend: [
    //     { label: 'Action', color: '#80CAFF', shape: 'rectangle' },
    //     { label: 'Decision', color: '#FFBDF2', shape: 'diamond' },
    //     { label: 'Start / End', color: '#FFCD29', shape: 'circle' },
    //   ],
    //   flowSteps: [
    //     { id: 1, type: 'start', label: 'Enter platform' },
    //     { id: 2, type: 'action', label: 'Click on create account' },
    //     { id: 3, type: 'action', label: 'Fill the required information' },
    //     { id: 4, type: 'action', label: 'Enter OTP received on mobile' },
    //     { id: 5, type: 'action', label: 'Congratulations message' },
    //     { id: 6, type: 'action', label: 'Onboarding screen' },
    //     { id: 7, type: 'action', label: 'Resend OTP' },
    //     { id: 8, type: 'end', label: 'End' },
    //   ],

    //   connections: [
    //     { from: 1, to: 2 },
    //     { from: 2, to: 3 },
    //     { from: 3, to: 2, label: 'Go back', fromSide: 'BOTTOM', toSide: 'BOTTOM' },
    //     { from: 3, to: 4 },
    //     { from: 4, to: 5, label: 'Successful OTP' },
    //     { from: 4, to: 7, label: 'Not received OTP', fromSide: 'BOTTOM', toSide: 'TOP' },
    //     { from: 5, to: 6 },
    //     { from: 6, to: 8 },
    //     { from: 7, to: 5, fromSide: 'RIGHT', toSide: 'BOTTOM' },
    //   ],
    // },

  };

  const visualizationLabels: Record<VisualizationType, string> = {
    howMightWe: 'How Might We',
    problemStatements: 'Problem Statements',
    interviewQuestions: 'Interview Questions',
    userStories: 'User Stories',
    surveyQuestions: 'Survey Questions',
    fiveWhys: 'Five Whys',
    persona: 'Persona',
    journeyMap: 'Journey Map',
    mindMap: 'Mind Map',
    // userFlowChart: 'User Flow Chart',
  };

  return (
    <div>
      <div style={{ padding: '20px', borderBottom: '1px solid #E5E5E5', backgroundColor: '#F5F5F5' }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
          Select Visualization Type (9 Total)
        </h2>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {(Object.keys(visualizationLabels) as VisualizationType[]).map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              style={{
                padding: '8px 16px',
                backgroundColor: selectedType === type ? '#0066FF' : 'white',
                color: selectedType === type ? 'white' : '#000',
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: selectedType === type ? '600' : '400',
              }}
            >
              {visualizationLabels[type]}
            </button>
          ))}
        </div>
      </div>

      <CanvasExportView type={selectedType} data={sampleData[selectedType]} />
    </div>
  );
};

export default CanvasExportDemo;
